import { useEffect, useState, useMemo } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import ActivityTrailMap from "./ActivityTrailMap";
import OwnerOverviewV2 from "./OwnerOverviewV2";
import { WorkforceReadinessPanel } from "./WorkforceReadinessPanel";
import {
  Radio, CalendarClock, TrendingUp, AlertTriangle, Users, MapPin,
  Clock, ChevronRight, Loader2, UserX,
} from "lucide-react";

function ymd(d) { return d.toISOString().slice(0, 10); }
function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday=0
  x.setDate(x.getDate() - day);
  return x;
}

function HorizonHeader({ icon: Icon, tone, kicker, title }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{kicker}</div>
        <h2 className="font-heading font-bold text-slate-900 text-lg leading-tight">{title}</h2>
      </div>
    </div>
  );
}

function StatPill({ label, value, tone = "slate", testid }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${map[tone]}`} data-testid={testid}>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="text-xs mt-1">{label}</div>
    </div>
  );
}

export default function ManagerOperationsHome({ onNavigate, onOpenFundraiser, readOnly = false }) {
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState([]);
  const [fortnight, setFortnight] = useState([]);

  useEffect(() => {
    const now = new Date();
    const todayStr = ymd(now);
    const wkStart = startOfWeek(now);
    const nextWeekEnd = new Date(wkStart); nextWeekEnd.setDate(nextWeekEnd.getDate() + 13);
    (async () => {
      setLoading(true);
      try {
        const [t, f] = await Promise.all([
          apiClient.get(`/schedules?startDate=${todayStr}&endDate=${todayStr}`),
          apiClient.get(`/schedules?startDate=${ymd(wkStart)}&endDate=${ymd(nextWeekEnd)}`),
        ]);
        setToday(t.data || []);
        setFortnight(f.data || []);
      } catch (e) {
        setToday([]); setFortnight([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const todayStats = useMemo(() => {
    const assigned = today.filter((s) => !s.isUnassigned);
    const out = new Set();
    assigned.forEach((s) => (s.userIds || []).forEach((u) => out.add(u)));
    return {
      outNow: out.size,
      shifts: today.length,
      unassigned: today.filter((s) => s.isUnassigned).length,
    };
  }, [today]);

  const { thisWeek, nextWeek, coverageGaps } = useMemo(() => {
    const now = new Date();
    const wkStart = startOfWeek(now);
    const thisWeekEnd = new Date(wkStart); thisWeekEnd.setDate(thisWeekEnd.getDate() + 6);
    const tw = []; const nw = [];
    fortnight.forEach((s) => {
      const d = new Date(s.date + "T00:00:00");
      if (d <= thisWeekEnd) tw.push(s); else nw.push(s);
    });
    return { thisWeek: tw, nextWeek: nw, coverageGaps: fortnight.filter((s) => s.isUnassigned) };
  }, [fortnight]);

  const ShiftRow = ({ s }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 text-sm" data-testid="ops-shift-row">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs text-slate-400 w-16 shrink-0">{s.date?.slice(5)}</span>
        <Clock className="h-3 w-3 text-slate-300 shrink-0" />
        <span className="text-xs text-slate-500 w-20 shrink-0">{s.startTime}-{s.endTime}</span>
        <span className="truncate text-slate-700">{s.locationName}</span>
        {s.campaignName && <span className="text-xs text-slate-400 truncate hidden md:inline">· {s.campaignName}</span>}
      </div>
      {s.isUnassigned
        ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 gap-1 shrink-0"><UserX className="h-3 w-3" />Unassigned</Badge>
        : <span className="text-xs text-slate-600 truncate max-w-[40%] text-right">{(s.userNames || []).join(", ")}</span>}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="manager-operations-home">
      {/* ===== A. LIVE NOW ===== */}
      <section data-testid="ops-now">
        <HorizonHeader icon={Radio} tone="bg-rose-100 text-rose-600" kicker="Live now" title="What's happening right now" />
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading live state…</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatPill label="Out today" value={todayStats.outNow} tone="emerald" testid="ops-out-now" />
            <StatPill label="Shifts today" value={todayStats.shifts} tone="indigo" testid="ops-shifts-today" />
            <StatPill label="Unassigned today" value={todayStats.unassigned} tone={todayStats.unassigned ? "rose" : "slate"} testid="ops-unassigned-today" />
            <div className="rounded-lg border border-slate-200 px-4 py-3 flex items-center justify-between bg-white">
              <div className="text-xs text-slate-500">Field activity</div>
              <Button variant="ghost" size="sm" className="h-7 gap-1 text-indigo-600" onClick={() => onNavigate?.("activity-locations")} data-testid="ops-open-heatmap">
                Heat map <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
        <Card className="border-slate-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-700">
              <MapPin className="h-4 w-4 text-rose-500" /> Live activity map
            </div>
            <ActivityTrailMap />
          </CardContent>
        </Card>
      </section>

      {/* ===== B. WHAT'S NEXT ===== */}
      <section data-testid="ops-next">
        <HorizonHeader icon={CalendarClock} tone="bg-indigo-100 text-indigo-600" kicker="What's next" title="This week & next week" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-slate-200" data-testid="ops-this-week">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-800">This week <span className="text-xs text-slate-400">({thisWeek.length} shifts)</span></span>
                <Button variant="ghost" size="sm" className="h-7 gap-1 text-indigo-600" onClick={() => onNavigate?.("schedule")} data-testid="ops-open-planner-1">
                  Shift Planner <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="max-h-56 overflow-y-auto">
                {thisWeek.length ? thisWeek.slice(0, 20).map((s) => <ShiftRow key={s.id} s={s} />)
                  : <p className="text-sm text-slate-400 py-4 text-center">No shifts scheduled this week.</p>}
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="ops-next-week">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-800">Next week <span className="text-xs text-slate-400">({nextWeek.length} shifts)</span></span>
                {coverageGaps.length > 0 && (
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 gap-1" data-testid="ops-coverage-gaps">
                    <AlertTriangle className="h-3 w-3" />{coverageGaps.length} coverage gap{coverageGaps.length === 1 ? "" : "s"}
                  </Badge>
                )}
              </div>
              <div className="max-h-56 overflow-y-auto">
                {nextWeek.length ? nextWeek.slice(0, 20).map((s) => <ShiftRow key={s.id} s={s} />)
                  : <p className="text-sm text-slate-400 py-4 text-center">No shifts scheduled next week.</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ===== C. CAMPAIGN TO DATE ===== */}
      <section data-testid="ops-to-date">
        <HorizonHeader icon={TrendingUp} tone="bg-emerald-100 text-emerald-600" kicker="To date" title="Campaign performance so far" />
        <OwnerOverviewV2 onNavigate={onNavigate} onOpenFundraiser={onOpenFundraiser} isManager={readOnly} />
      </section>

      {/* ===== D. ATTENTION / ACTION QUEUE ===== */}
      <section data-testid="ops-actions">
        <HorizonHeader icon={AlertTriangle} tone="bg-amber-100 text-amber-600" kicker="Attention" title="Action queue" />
        <WorkforceReadinessPanel />
      </section>
    </div>
  );
}
